import { Element, useEditor, useNode } from "@craftjs/core";
import * as React from "react";
import type { Speaker } from "src/types";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import Text from "@/components/editor/primitives/text/text-component";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
  BlockPaddingWrapper,
} from "@/components/editor/shared/block-wrapper";
import SpeakersSettings from "@/components/editor/shared/speakers/speakers-settings/speakers-settings-component";

import SpeakerCard from "./speaker-card/speaker-card-component";
import SpeakersList from "./speakers-list-component";

/**
 * `SPEAKERS_LIST_ID` is the id we use to identify the speakers list component,
 * useful when we need to append SpeakerCards to the list.
 */

export type SpeakerLayout = "large-cards";

type SpeakersProps = {
  speakers?: Speaker[];
  layout: SpeakerLayout;
};

export default function Speakers(props: SpeakersProps) {
  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const {
    connectors: { connect },
    nodeId,
  } = useNode((node) => {
    return {
      nodeId: node.id,
    };
  });

  return (
    <BlockBackgroundColorWrapper ref={connect} id={nodeId}>
      <BlockPaddingWrapper>
        <BlockMaxWidthWrapper>
          <div className="flex justify-center">
            <Element
              id="speakers-title"
              is={Text}
              element="h2"
              text="Speakers"
              pattern="headingTwo"
              data-testid="speakers-title"
            />
          </div>
          <Element
            id="speakers-list"
            is={SpeakersList}
            layout={props.layout}
            canvas={enabled}
          >
            {props.speakers?.map((speaker) => (
              <SpeakerCard
                key={speaker.id + speaker.name}
                speaker={speaker}
                layout={props.layout}
                isBioAvailable={!!speaker.bio}
              />
            ))}
          </Element>
        </BlockMaxWidthWrapper>
      </BlockPaddingWrapper>
    </BlockBackgroundColorWrapper>
  );
}

Speakers.craft = {
  custom: {
    displayName: "Speakers",
  },
  related: {
    leftOutlineOptions: TopLevelFloatingOptions,
    settings: () => SpeakersSettings({ template: SpeakerCard }),
  },
};
